import * as React from "react";
import classes from "./ProjectInformation.module.css";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import {
  getAllClients,
  getProgramsByClientId,
  getAllProjectsByClientIdAndProgramId,
  getAllDepartments,
  getAllJobNames,
} from "services/centerPointUserServices";
import Autocomplete from "@mui/material/Autocomplete";
import { useDispatch, useSelector } from "react-redux";
import { addNonHRMSEmployeeActions } from "Store/addNonHRMSEmployee";

import DateField from "components/Common/DateField";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import { format } from "date-fns";
import { IconButton, Tooltip } from "@mui/material";
import { Clear } from "@mui/icons-material";

const ProjectInformation = (props) => {
  const { errorHandler } = props;
  const dispatch = useDispatch();
  const details = useSelector((state) => state.addNonHRMSEmployee);
  const [clientArray, setClientArray] = React.useState([]);
  const [programArray, setProgramArray] = React.useState([]);
  const [projectArray, setProjectArray] = React.useState([]);
  const [department, setDepartment] = React.useState([]);
  const [dateTextHandler, setDateTextHandler] = React.useState({
    productTraining: "text",
    nestingStart: "text",
    projectStart: "text",
    projectEnd: "text",
    productionStart: "text",
  });
  const [error, setError] = React.useState(false);

  const fetchData = async () => {
    dispatch(addNonHRMSEmployeeActions.updateProjectInformationLoader(true));
    try {
      const [clientResponse, departmentResponse, jobResponse] =
        await Promise.all([
          getAllClients(),
          getAllDepartments(),
          getAllJobNames(),
        ]);

      if (clientResponse?.data?.message === "Success") {
        setClientArray(clientResponse?.data?.data);
      } else {
        errorHandler(true, "Clients could not be fetched.", "error");
      }

      if (departmentResponse?.data?.message === "Success") {
        setDepartment(departmentResponse?.data?.data);
      } else {
        errorHandler(true, "Departments could not be fetched.", "error");
      }
    } catch (error) {
      console.error("Something went wrong", error);
    }
    dispatch(addNonHRMSEmployeeActions.updateProjectInformationLoader(false));
  };

  const handleDates = (dateType, elementType) => {
    setDateTextHandler((prev) => {
      return {
        ...prev,
        [dateType]: elementType,
      };
    });
  };

  const handleClientChange = async (event, value) => {
    dispatch(addNonHRMSEmployeeActions.updateProjectInformationLoader(true));
    dispatch(addNonHRMSEmployeeActions.updateClient(value));
    dispatch(
      addNonHRMSEmployeeActions.updateProgram({
        ProgramName: "",
        ProgramCode: "",
      }),
    );
    dispatch(
      addNonHRMSEmployeeActions.updateProject({
        ProjectName: "",
        ProjectCode: "",
      }),
    );

    try {
      if (value !== null) {
        const programResponse = await getProgramsByClientId(value?.ClientId);
        if (programResponse?.data?.message === "Success") {
          setProgramArray(programResponse?.data?.data);
          setProjectArray([]);
        } else errorHandler(true, "Programs could not be fetched.", "error");
      } else {
        setProgramArray([]);
        setProjectArray([]);
      }
    } catch (error) {
      errorHandler(true, "Some internal error occured.", "error");
    } finally {
      dispatch(addNonHRMSEmployeeActions.updateProjectInformationLoader(false));
    }
  };

  const handleProgramChange = async (event, value) => {
    dispatch(addNonHRMSEmployeeActions.updateProjectInformationLoader(true));
    dispatch(addNonHRMSEmployeeActions.updateProgram(value));
    dispatch(
      addNonHRMSEmployeeActions.updateProject({
        ProjectName: "",
        ProjectCode: "",
      }),
    );

    try {
      if (value !== null) {
        const projectResponse = await getAllProjectsByClientIdAndProgramId(
          details.client.ClientId,
          value?.ProgramId,
        );
        if (projectResponse?.data?.message === "Success")
          setProjectArray(projectResponse?.data?.data);
        else {
          errorHandler(true, "Projects could not be fetched.", "error");
        }
      } else {
        setProjectArray([]);
      }
    } catch (error) {
      errorHandler(true, "Some internal error occured.", "error");
    } finally {
      dispatch(addNonHRMSEmployeeActions.updateProjectInformationLoader(false));
    }
  };

  React.useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        await fetchData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Grid container spacing={1} className={classes.gridContainer}>
      <Grid item xs={3}>
        <Autocomplete
          disablePortal
          blurOnSelect
          options={clientArray}
          disabled={details.disableElements}
          value={details.client}
          onChange={handleClientChange}
          className={classes.rightAutoFieldWidth}
          getOptionLabel={(option) =>
            option?.ClientName === "" && option?.ClientCode === ""
              ? ""
              : `${option?.ClientName + " (" + option?.ClientCode + ")"}`
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              disabled={details.disableElements}
              label="Client"
              required
            />
          )}
        />
      </Grid>
      <Grid item xs={3}>
        <Autocomplete
          disablePortal
          blurOnSelect
          options={programArray}
          disabled={details.disableElements}
          value={details.program}
          className={classes.rightAutoFieldWidth}
          onChange={handleProgramChange}
          getOptionLabel={(option) =>
            option.ProgramName === "" && option.ProgramCode === ""
              ? ""
              : `${option?.ProgramName + " (" + option?.ProgramCode + ")"}`
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Program" required />
          )}
        />
      </Grid>

      <Grid item xs={3}>
        <Autocomplete
          disablePortal
          disabled={details.disableElements}
          blurOnSelect
          options={projectArray}
          value={details.project}
          className={classes.rightAutoFieldWidth}
          onChange={(e, inputValue) =>
            dispatch(addNonHRMSEmployeeActions.updateProject(inputValue))
          }
          getOptionLabel={(option) =>
            option?.ProjectName === "" && option?.ProjectCode === ""
              ? ""
              : `${option?.ProjectName + " (" + option?.ProjectCode + ")"}`
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Project" required />
          )}
        />
      </Grid>
      <Grid item xs={3}>
        {" "}
        <TextField
          id="outlined-basic"
          variant="outlined"
          label="Wave"
          size="small"
          fullWidth
          type="number"
          value={details.wave}
          disabled={details.disableElements}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
          onChange={(e) => {
            if (e.target.value >= 0 || e.target.value === "")
              dispatch(addNonHRMSEmployeeActions.updateWave(e.target.value));
          }}
        />
      </Grid>

      <Grid item xs={3}>
        <Autocomplete
          disablePortal
          blurOnSelect
          disabled={details.disableElements}
          options={department}
          value={details.department}
          onChange={(e, value) =>
            dispatch(addNonHRMSEmployeeActions.updateDepartment(value))
          }
          className={classes.rightAutoFieldWidth}
          getOptionLabel={(option) =>
            option?.Name === "" && option?.Code === ""
              ? ""
              : `${option?.Name + " (" + option?.Code + ")"}`
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Department" required />
          )}
        />
      </Grid>
      <Grid item xs={9}></Grid>

      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Product Training Start Date"
            value={
              details.productTrainingStartDate === ""
                ? null
                : moment(details.productTrainingStartDate)
            }
            disabled={details.disableElements}
            onChange={(newValue) => {
              if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                dispatch(
                  addNonHRMSEmployeeActions.updateProductTrainingStartDate(
                    format(newValue, "yyyy-MM-dd"),
                  ),
                );
              }
            }}
            format="yyyy-MM-dd"
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={false}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {details.productTrainingStartDate && (
                        <Tooltip title={"Clear"}>
                          <IconButton
                            onClick={() =>
                              dispatch(
                                addNonHRMSEmployeeActions.updateProductTrainingStartDate(
                                  "",
                                ),
                              )
                            }
                            edge="end"
                          >
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Nesting Start Date"
            value={
              details.nestingStartDate === ""
                ? null
                : moment(details.nestingStartDate)
            }
            disabled={details.disableElements}
            onChange={(newValue) => {
              if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                dispatch(
                  addNonHRMSEmployeeActions.updateNestingStartDate(
                    format(newValue, "yyyy-MM-dd"),
                  ),
                );
              }
            }}
            format="yyyy-MM-dd"
            renderInput={(params) => (
              <TextField
                {...params}
                error={false}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {details.nestingStartDate && (
                        <Tooltip title={"Clear"}>
                          <IconButton
                            onClick={() =>
                              dispatch(
                                addNonHRMSEmployeeActions.updateNestingStartDate(
                                  "",
                                ),
                              )
                            }
                            edge="end"
                          >
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Project Start Date"
            value={
              details.projectStartDate === ""
                ? null
                : moment(details.projectStartDate)
            }
            disabled={details.disableElements}
            onChange={(newValue) => {
              if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                dispatch(
                  addNonHRMSEmployeeActions.updateProjectStartDate(
                    format(newValue, "yyyy-MM-dd"),
                  ),
                );
              }
            }}
            format="yyyy-MM-dd"
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={false}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {details.projectStartDate && (
                        <Tooltip title={"Clear"}>
                          <IconButton
                            onClick={() =>
                              dispatch(
                                addNonHRMSEmployeeActions.updateProjectStartDate(
                                  "",
                                ),
                              )
                            }
                            edge="end"
                          >
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Project End Date"
            value={
              details.projectEndDate === ""
                ? null
                : moment(details.projectEndDate)
            }
            disabled={details.disableElements}
            onChange={(newValue) => {
              if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                dispatch(
                  addNonHRMSEmployeeActions.updateProjectEndDate(
                    format(newValue, "yyyy-MM-dd"),
                  ),
                );
              }
            }}
            format="yyyy-MM-dd"
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={false}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {details.projectEndDate && (
                        <Tooltip title={"Clear"}>
                          <IconButton
                            onClick={() =>
                              dispatch(
                                addNonHRMSEmployeeActions.updateProjectEndDate(
                                  "",
                                ),
                              )
                            }
                            edge="end"
                          >
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Production Start Date"
            value={
              details.productionStartDate === ""
                ? null
                : moment(details.productionStartDate)
            }
            disabled={details.disableElements}
            onChange={(newValue) => {
              if (newValue && newValue instanceof Date && !isNaN(newValue)) {
                dispatch(
                  addNonHRMSEmployeeActions.updateProductionStartDate(
                    format(newValue, "yyyy-MM-dd"),
                  ),
                );
              }
            }}
            format="yyyy-MM-dd"
            renderInput={(params) => (
              <TextField
                {...params}
                required
                error={false}
                fullWidth
                size="small"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {details.productionStartDate && (
                        <Tooltip title={"Clear"}>
                          <IconButton
                            onClick={() =>
                              dispatch(
                                addNonHRMSEmployeeActions.updateProductionStartDate(
                                  "",
                                ),
                              )
                            }
                            edge="end"
                          >
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
    </Grid>
  );
};

export default ProjectInformation;
